import { useEffect, useState } from 'react';
import usePrevious from 'react-use/esm/usePrevious';
import {
  createPixelDataSharedWorker,
  providerMap,
} from 'modules/viewer/workers/PixelDataSharedWorker';
import type { PixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import { AppRoutePath } from 'config/constants';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { atom, useRecoilState, useRecoilValue } from 'recoil';
import { generationStateAtom } from './EscapeHatch/state';
import { useEscapeHatchCoordination, TAB_ID } from './EscapeHatch/coordination';
import { readMissedPongs, writeMissedPongs, clearMissedPongs } from './EscapeHatch/missedPongStore';
import { markUnmetDemand, clearUnmetDemand } from './EscapeHatch/unmetDemandStore';
import { selectGeneration } from './EscapeHatch/policy';
import { awaitPriorGenerationDrained } from './EscapeHatch/generationDrain';
import {
  findStalledLoadingStack,
  updateFirstSeenLoadingAt,
  IDLE_DELIVERY_STALL_MS,
  IDLE_COLD_START_STALL_MS,
  type LoadingProviderSnapshot,
} from './EscapeHatch/idleDeliveryWatchdog';
import type { RecoilState } from 'recoil';
import { openWindowEvent, dispatchExtensionEvent } from 'domains/extension/extensionEventCreators';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { env } from 'config/env';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags/suspense';
import { logger } from 'modules/logger';
const terminateSharedWorkerConnection = (worker: PixelDataSharedWorker | null) => {
  if (worker == null) {
    return;
  }

  // postMessage as well as close can throw on an already-closed MessagePort, so guard both.
  try {
    worker.port.postMessage({ type: 'terminate-connection' });
    worker.port.close();
  } catch (error) {
    logger.info(
      `failed to terminate pixel data shared worker port: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};

export const pixelWorkerOpenedState: RecoilState<boolean> = atom({
  key: 'viewer.dre.pixelWorkerOpenState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const useOpenPixelDataSharedWorker = (workerKey: string): PixelDataSharedWorker | null => {
  const [hasWorkerOpened, setWorkerOpened] = useRecoilState(pixelWorkerOpenedState);
  const useDedupedPixelWorker = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);
  // VX-4156 kill-switch: when off, the client does not post `enable-pruning` (so the SharedWorker's
  // prune() never marks this port eligible to reclaim) and the watchdog below is disabled.
  // Worker-side prune()/reconcile is unchanged — it cannot read flags — but with no port opted in it
  // has nothing to act on for this connection.
  const enablePortPruning = useFeatureFlagEnabled(FF.VIEWER_SW_PORT_PRUNING);
  // VX-4302: connect at the current generation when the escape hatch is enabled, so a committed bump
  // (a brand-new worker name) is picked up. When off, the generation stays 0 (the bare name) and
  // behaviour is identical to today. selectGeneration is the shared flag-gate the host window uses
  // too, so the two windows never disagree on which worker to connect to.
  const generationBumpEnabled = useFeatureFlagEnabled(FF.VIEWER_SW_GENERATION_BUMP);
  const generationState = useRecoilValue(generationStateAtom);
  const activeGeneration = selectGeneration(generationBumpEnabled, generationState);

  useEffect(() => {
    if (!hasWorkerOpened) {
      const isFocused = document.hasFocus();
      if (isWorkspaceManagerAvailable()) {
        const { protocol, host } = window.location;

        const url = `${protocol}//${host}${AppRoutePath.PIXEL_WORKER}`;
        dispatchExtensionEvent(
          openWindowEvent({
            url,
            windowConfig: {
              // `focused` disrupts functionality of `minimized` state
              // see https://issues.chromium.org/issues/40204304
              focused: null,
              top: null,
              left: null,
              width: null,
              height: null,
              state: 'minimized',
            },
          })
        );
        // generally not needed, but a fallback in case the new window somehow steals focus
        if (isFocused) {
          window.focus();
        }
      } else {
        if (env.STORYBOOK === 'true') {
          return;
        }

        window.open(
          AppRoutePath.PIXEL_WORKER,
          AppRoutePath.PIXEL_WORKER,
          'noopener=yes, width=300, height=150, left=0, top=0, toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no'
        );
        if (isFocused) {
          window.focus();
        }
      }

      // Viewer functionality is absolutely dependent on the Shared Worker opening.
      // In the event the dedicated window cannot open, update the state so that
      // the Viewer windows will start talking to the Shared Worker, which will
      // start a new Shared Worker if not already present.
      setTimeout(() => {
        setWorkerOpened(true);
      }, 1500);
    }
  }, [hasWorkerOpened, setWorkerOpened]);

  const [pixelDataSharedWorkerMap, setPixelDataSharedWorkerMap] = useState<{
    [caseSmid: string]: PixelDataSharedWorker | null;
  }>({ '': null });

  const matchingWorker = pixelDataSharedWorkerMap[workerKey] ?? null;

  // VX-4385: while a generation bump is in flight, the prior generation we are waiting to confirm
  // drained before we connect at the new generation; null when no bump-driven gate is pending. This
  // gates ONLY the bump-driven recreation — the watchdog (same-generation) reconnect path never sets
  // it, so a severed-port recovery still reconnects immediately.
  const [drainGatePriorGeneration, setDrainGatePriorGeneration] = useState<number | null>(null);

  // VX-4302 #5b: the automatic ladder trigger. Counts watchdog recreates that fail to restore
  // delivery (the watchdog now fires on VX-4284's delivery-stall pong-withhold) and, when quorum of
  // eligible observers agree, commits a generation bump. No-op while the flag is off. The callback is
  // stable (a useRecoilCallback) except when `enabled` toggles, so the watchdog effect can depend on
  // it directly.
  const { reportFailedRecreate } = useEscapeHatchCoordination({
    matchingWorker,
    enabled: generationBumpEnabled,
    activeGeneration,
  });

  useEffect(() => {
    // VX-4385: hold off creating the new-generation worker (which would immediately start decoding a
    // fresh OpenJPH WASM heap) until the prior generation has drained. The drain-await effect below
    // clears the gate on the retirement ack or a bounded deadline, after which this effect re-runs and
    // connects. The initial connection and the watchdog reconnect leave the gate null, so neither is
    // delayed.
    if (drainGatePriorGeneration != null) return;
    if (hasWorkerOpened && matchingWorker == null) {
      setPixelDataSharedWorkerMap({
        [workerKey]: createPixelDataSharedWorker(
          useDedupedPixelWorker,
          undefined,
          enablePortPruning,
          activeGeneration
        ),
      });
      logger.info(`new shared worker connection for tab=${TAB_ID}`);
    }
  }, [
    hasWorkerOpened,
    useDedupedPixelWorker,
    enablePortPruning,
    matchingWorker,
    workerKey,
    activeGeneration,
    drainGatePriorGeneration,
  ]);

  // VX-4302: a committed generation bump — from this tab, or message-driven via the
  // BroadcastChannel-synced generation atom from another tab — drops the old-generation port so the
  // create effect above reconnects at the new generation. Reuses the load-bearing `worker -> null ->
  // worker` transition that useReinitializeProvidersOnWorkerChange keys on (VX-4223). There is no
  // timer in this path, so a backgrounded tab reconnects as soon as it processes the message.
  const previousGeneration = usePrevious(activeGeneration);
  useEffect(() => {
    // usePrevious is undefined on the first render and auto-advances every render, so a generation
    // change observed while the worker is null is naturally "consumed" without a spurious reconnect.
    if (previousGeneration === undefined || previousGeneration === activeGeneration) return;
    if (matchingWorker == null) return;
    logger.info(`SW generation -> ${activeGeneration} for tab=${TAB_ID}; reconnecting`);
    // VX-4385: gate the reconnect on the prior generation draining (set the gate to the generation we
    // are leaving). The create effect skips while this is set; the drain-await effect clears it once
    // the prior generation acks retirement or the deadline elapses, so the new generation never starts
    // decoding while the old generation's WASM heap is still resident.
    setDrainGatePriorGeneration(previousGeneration);
    // Null the worker; the [matchingWorker] cleanup below performs the single terminate/close so the
    // port is not closed twice (a second postMessage on a closed port can throw).
    setPixelDataSharedWorkerMap({ [workerKey]: null });
  }, [activeGeneration, previousGeneration, matchingWorker, workerKey]);

  // VX-4385: resolve the bump-driven drain gate. Waits for the prior generation to confirm it has
  // released its memory (the retirement ack) or for a bounded deadline, then clears the gate so the
  // create effect connects at the new generation. Bounded so recovery is never wedged if no prior
  // worker is listening (e.g. it was already gone, or BroadcastChannel is unavailable).
  useEffect(() => {
    if (drainGatePriorGeneration == null) return;
    let cancelled = false;
    void awaitPriorGenerationDrained(drainGatePriorGeneration).then(() => {
      if (!cancelled) setDrainGatePriorGeneration(null);
    });
    return () => {
      cancelled = true;
    };
  }, [drainGatePriorGeneration]);

  // cancel the current connection if it is replaced
  useEffect(() => {
    return () => {
      terminateSharedWorkerConnection(matchingWorker);
    };
  }, [matchingWorker]);

  // VX-4156: ping/pong watchdog. If two consecutive pings receive no pong, null out the matching
  // worker so the existing effect recreates it. Gated by the VIEWER_SW_PORT_PRUNING kill-switch:
  // when off, the watchdog never arms and the worker is never recreated on a missed pong.
  useEffect(() => {
    if (!enablePortPruning || matchingWorker == null) return;

    // VX-4834 follow-up: hydrate from the reload-durable store rather than starting at 0. Every
    // worklist case-open is a hard navigation, so a per-page counter could never reach the 2 misses
    // this watchdog requires (~70-80s of ping/pong) and the ladder above it never received even one
    // increment. The store voids the count on a generation change and after its own TTL, so an
    // inherited value only ever represents a still-live wedge on this same worker lineage.
    let missedPongCount = readMissedPongs({
      tabId: TAB_ID,
      generation: activeGeneration,
      now: Date.now(),
    });
    // Every place the in-memory count resets must drop the persisted copy too, or a reload would
    // resurrect a miss that this page already proved stale.
    const resetMissedPongCount = () => {
      missedPongCount = 0;
      clearMissedPongs({ tabId: TAB_ID });
    };
    let pendingPongTimeoutId: ReturnType<typeof setTimeout> | null = null;
    // Declared before recreateWorker so the teardown can stop the ping loop immediately; assigned
    // when the interval is armed below.
    let pingIntervalId: ReturnType<typeof setInterval> | null = null;
    // VX-4395: first-observed-loading timestamps for the idle-delivery check below, scoped to this
    // connection's lifetime. Still per-page on purpose: it is keyed by stackSmid and a case-open
    // brings genuinely new stacks, so there is nothing meaningful to carry across. The cross-reload
    // equivalent is the tab-scoped unmet-demand record below.
    const firstSeenLoadingAt = new Map<string, number>();
    // VX-4834 follow-up: how many consecutive ticks THIS page has seen unmet demand. The durable
    // record can be inherited already past the stall window, so without a local floor a freshly
    // opened page could recreate the worker on its very first tick — which a radiologist clicking
    // quickly through several briefly-starved cases would trigger routinely. Requiring two ticks
    // means this page has itself been starved for ~a minute before it acts on inherited evidence.
    let localUnmetDemandTicks = 0;

    // Null out the matching worker so the create effect rebuilds it. This null transition is
    // load-bearing: useReinitializeProvidersOnWorkerChange treats oldWorker -> null -> newWorker as
    // the recovery signal that rebinds existing providers to the new port. A rebuild that skips null
    // would look like referential churn and the page would silently stop self-healing (VX-4223). The
    // [matchingWorker] cleanup below performs the single terminate/close so the port is not closed
    // twice (a second postMessage on a closed port can throw). Shared by the missed-pong path and the
    // worker-initiated pong-rejected path below.
    const recreateWorker = () => {
      // Stop the ping loop before closing the port so the next 30s tick can't postMessage on the
      // just-terminated port (the effect cleanup also clears it, but only after the next render).
      if (pingIntervalId != null) {
        clearInterval(pingIntervalId);
        pingIntervalId = null;
      }
      if (pendingPongTimeoutId != null) {
        clearTimeout(pendingPongTimeoutId);
        pendingPongTimeoutId = null;
      }
      resetMissedPongCount();
      // A recreate restarts the delivery attempt, so the stall window must restart with it —
      // otherwise the already-elapsed window would re-fire on every subsequent tick.
      clearUnmetDemand({ tabId: TAB_ID });
      setPixelDataSharedWorkerMap({ [workerKey]: null });
    };

    const onMessage = (e: MessageEvent) => {
      const data = (e as MessageEvent<{ type?: string; reason?: string }>).data;
      const type = data?.type;
      if (type === 'pong') {
        if (pendingPongTimeoutId != null) {
          clearTimeout(pendingPongTimeoutId);
          pendingPongTimeoutId = null;
        }
        resetMissedPongCount();
        return;
      }
      // VX-4284: the worker has detected its own delivery wedge (orphaned port, or frame delivery
      // stalled) and is asking to be recreated now, rather than making us wait out two missed pongs
      // (~80s). This is an explicit worker decision, not a possibly-throttled missed timer, so we
      // recover immediately regardless of tab visibility.
      if (type === 'pong-rejected') {
        const reason = data?.reason ?? 'unknown';
        logger.warn(`SW pong rejected (${reason}) for tab=${TAB_ID}; recreating worker`);
        // VX-4388: a pong-rejected means the worker itself declares this connection wedged — the same
        // "recreate didn't restore delivery" signal the missed-pong path feeds to the ladder, only sooner
        // and more explicit. Count it too (before recreating) so a wedge that a same-generation recreate can't fix
        // escalates to a generation bump instead of livelocking on the same gen, spawning duplicate
        // same-generation workers (allied-beige-superior). observeFrame resets the counter on recovery, so
        // a healthy self-heal (a re-registered orphaned port) never accumulates. No-op while the flag is off.
        reportFailedRecreate();
        recreateWorker();
      }
    };
    matchingWorker.port.addEventListener('message', onMessage);

    // VX-4223: a pong timeout armed while hidden can fire just after the tab becomes visible —
    // its lateness is background timer throttling, not a severed port. On the hidden->visible
    // transition, discard that stale timeout and reset the miss count so recovery requires fresh
    // consecutive misses observed entirely while visible (the in-callback guard below covers
    // timeouts that fire while still hidden).
    //
    // The reset is deliberately NOT gated on `pendingPongTimeoutId != null` like the clearTimeout
    // above it, and now that the count is durable it deliberately clears the persisted copy too.
    // VX-4223's rule is about the recorded miss, not about a timer being in flight: a miss whose
    // window overlapped a hidden period may be a throttling artifact, so it must not combine with a
    // post-unhide miss to recreate a healthy connection. Keeping the durable copy here would let a
    // reload after a hidden period resurrect exactly the evidence that rule discards, so the two have
    // to be cleared together. Covered by "a missed pong before hiding does not carry across the
    // hidden period (VX-4223)". This costs nothing for the wedge this store targets: a hard
    // navigation fires no visibilitychange, and a hidden tab cannot accrue misses at all.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (pendingPongTimeoutId != null) {
        clearTimeout(pendingPongTimeoutId);
        pendingPongTimeoutId = null;
      }
      resetMissedPongCount();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    pingIntervalId = setInterval(() => {
      try {
        matchingWorker.port.postMessage({ type: 'ping' });
      } catch {
        // port may be closed mid-cycle — treat as a missed pong
      }
      pendingPongTimeoutId = setTimeout(() => {
        // VX-4223: a hidden tab has throttled/frozen timers, so a late pong is expected and
        // not a sign of a severed port. Don't count a miss while hidden, and clear any miss
        // accrued before hiding so a stale pre-hide miss can't combine with a post-unhide one
        // to recreate a still-healthy connection. Require fresh consecutive misses once visible.
        if (document.visibilityState === 'hidden') {
          resetMissedPongCount();
          return;
        }
        missedPongCount += 1;
        // Persist before acting: if this miss completes the pair the recreate path clears the store
        // immediately, and if it doesn't, the next page life needs to inherit it.
        writeMissedPongs({
          tabId: TAB_ID,
          generation: activeGeneration,
          count: missedPongCount,
          now: Date.now(),
        });
        // Until now a single miss left no trace anywhere: only the >=2 recreate below logged, so a run
        // that never reached the pair was indistinguishable from one where no pong was ever missed.
        // EXP-011 hit exactly that -- zero "SW port stale" across ~240 wedged cases told us the pair
        // was never reached but not whether any miss occurred at all. Log every recorded miss so the
        // ladder's accumulation is observable (logger forwards to Datadog on any non-local deploy).
        // Correlate on TAB_ID, not workerKey: workerKey is the caseSmid (ViewerContext.tsx:412), a case
        // identifier that has no business in a log line. TAB_ID is a random per-browsing-context nanoid,
        // so it identifies nothing while actually being the better key here — it separates viewer/0 from
        // viewer/1, which reading these logs otherwise requires inferring from @view.url.
        logger.info(
          `SW missed pong ${missedPongCount}/2 for tab=${TAB_ID} at generation=${activeGeneration}`
        );
        if (missedPongCount >= 2) {
          logger.warn(`SW port stale for tab=${TAB_ID}; recreating worker`);
          // VX-4302 #5b: this recreate did not restore delivery — count it toward the ladder. At
          // quorum the coordination commits a generation bump. No-op while the flag is off.
          reportFailedRecreate();
          recreateWorker();
        }
      }, 10_000);

      // VX-4395: independent of ping/pong health — a worker can pong healthy (activeRequests===0
      // on its side) while a stack this page is still waiting on has silently stopped receiving
      // frames (the "idle-but-healthy" wedge; VX-4284's worker-side gate cannot see it because
      // nothing is outstanding from the worker's point of view). Checked on the same 30s cadence,
      // from the page's own delivery clock, so a worker that has gone quiet cannot self-report
      // healthy.
      const now = Date.now();
      const loadingStackSmids = new Set<string>();
      const loadingProviders: LoadingProviderSnapshot[] = [];
      // VX-4834: count how many loading stacks have actually ASKED the worker for pixels. STRESS
      // failing cases show 8-20 series with zero SharedWorker requests on a port the worker reports
      // healthy, while cases at the SAME port state load fine — so the port is not the discriminator
      // and "asked and got nothing" was indistinguishable from "never asked". These two counters
      // separate them.
      let loadingWithoutRequest = 0;
      providerMap.forEach((provider) => {
        if (!provider.isLoading()) return;
        const stackSmid = provider.stack.smid;
        loadingStackSmids.add(stackSmid);
        if (!provider.hasRequestedFrames()) loadingWithoutRequest += 1;
        loadingProviders.push({
          stackSmid,
          msSinceLastFrameDelivered: provider.msSinceLastFrameDelivered(),
        });
      });
      // Log only the anomaly — every loading stack silent — so a healthy tick stays quiet. Counts
      // only, no stack or case identifiers: correlate on tab + generation (see the missed-pong log).
      if (loadingProviders.length > 0 && loadingWithoutRequest === loadingProviders.length) {
        logger.warn(
          `SW demand without request: ${loadingWithoutRequest}/${loadingProviders.length} loading ` +
            `stacks have posted no load-stack/load-frame for tab=${TAB_ID} at ` +
            `generation=${activeGeneration}`
        );
      }
      updateFirstSeenLoadingAt(loadingStackSmids, firstSeenLoadingAt, now);
      const stalledStackSmid = findStalledLoadingStack(loadingProviders, firstSeenLoadingAt, now);
      if (stalledStackSmid != null) {
        logger.warn(
          `SW idle-but-healthy delivery stall on stack ${stalledStackSmid} for tab=${TAB_ID}; recreating worker`
        );
        // Same ladder-feeding signal as the missed-pong and pong-rejected paths: this recreate did
        // not (yet) prove delivery restored, so count it toward the escalation quorum.
        reportFailedRecreate();
        recreateWorker();
        return;
      }

      // VX-4834 follow-up: the cross-reload cold-start rung. The per-stack check above cannot see a
      // worklist wedge — firstSeenLoadingAt is keyed by stackSmid and every case-open brings new
      // stacks, so its 180s window restarts from zero on each case while EXP-011's cases lived 7-60s.
      // Measure the durable thing instead: how long has THIS TAB had demand the worker never
      // answered. That survives a case-open, and only a delivered frame — the one thing that
      // actually disproves a wedge — clears it.
      const hasDemand = loadingProviders.length > 0;
      // Any loading stack receiving frames inside the warm-path gate proves the worker is alive, so a
      // case that loads normally resets the window even if others around it failed. In a trickle
      // wedge the clock therefore accumulates across a run of starved cases and resets on each
      // success, which is the correct reading: a success genuinely disproves a total wedge.
      const anyRecentDelivery = loadingProviders.some(
        (p) =>
          p.msSinceLastFrameDelivered != null &&
          p.msSinceLastFrameDelivered <= IDLE_DELIVERY_STALL_MS
      );
      if (!hasDemand || anyRecentDelivery) {
        localUnmetDemandTicks = 0;
        clearUnmetDemand({ tabId: TAB_ID });
        return;
      }
      localUnmetDemandTicks += 1;
      const unmetSince = markUnmetDemand({
        tabId: TAB_ID,
        generation: activeGeneration,
        now,
      });
      const unmetForMs = now - unmetSince;
      logger.info(
        `SW unmet demand ${Math.round(unmetForMs / 1000)}s (localTicks=${localUnmetDemandTicks}) ` +
          `for tab=${TAB_ID} at generation=${activeGeneration}`
      );
      // Two local ticks before acting on a possibly-inherited origin — see localUnmetDemandTicks.
      if (localUnmetDemandTicks >= 2 && unmetForMs > IDLE_COLD_START_STALL_MS) {
        logger.warn(
          `SW unmet demand across reloads for ${Math.round(unmetForMs / 1000)}s for ` +
            `tab=${TAB_ID}; recreating worker`
        );
        reportFailedRecreate();
        recreateWorker();
      }
    }, 30_000);

    return () => {
      if (pingIntervalId != null) clearInterval(pingIntervalId);
      if (pendingPongTimeoutId != null) clearTimeout(pendingPongTimeoutId);
      matchingWorker.port.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enablePortPruning, matchingWorker, workerKey, reportFailedRecreate, activeGeneration]);

  // cancel the page's current connection as the page closes
  useEffect(() => {
    function terminateConnection() {
      terminateSharedWorkerConnection(matchingWorker);
    }

    window.addEventListener('beforeunload', terminateConnection);
    return () => window.removeEventListener('beforeunload', terminateConnection);
  }, [matchingWorker]);

  return matchingWorker;
};
